import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';
import { toggleArticleFeature } from './getKbArticles';

export const fetchFeaturedArticles = createAsyncThunk(
    'featuredArticles/fetchFeaturedArticles',
    async ({ business_id, group_id }, { getState, rejectWithValue }) => {
        try {
            const { loginToken } = getState();
            const authToken = loginToken?.response?.token;

            if (!business_id) {
                throw new Error('Business ID not found');
            }

            const url = `${ROOT_URL}/kb/articles/feature/${business_id}`;

            const response = await axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                },
                params: group_id ? { group_id } : undefined
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching featured articles:', error);
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch featured articles'
            );
        }
    }
);

const initialState = {
    loading: true,
    error: null,
    articles: [],
    hasFetched: false,
    lastFetchedBusinessId: null,
    lastFetchedGroupId: null,
};

const featuredArticlesSlice = createSlice({
    name: 'featuredArticles',
    initialState,
    reducers: {
        resetFeaturedArticles: (state) => {
            return initialState;
        },
        clearFeaturedError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFeaturedArticles.pending, (state, action) => {
                const businessId = action.meta?.arg?.business_id ?? null;
                const groupId = action.meta?.arg?.group_id ?? null;
                const sameBusiness = String(state.lastFetchedBusinessId) === String(businessId);
                const sameGroup =
                    state.lastFetchedGroupId != null &&
                    String(state.lastFetchedGroupId) === String(groupId);
                // Soft loading on revisit for same business+group: keep existing articles visible.
                state.loading = !(state.hasFetched && sameBusiness && sameGroup);
                if (!(sameBusiness && sameGroup) && state.hasFetched) {
                    state.articles = [];
                }
                state.error = null;
            })
            .addCase(fetchFeaturedArticles.fulfilled, (state, action) => {
                const businessId = action.meta?.arg?.business_id ?? null;
                const groupId = action.meta?.arg?.group_id ?? null;
                state.loading = false;
                state.error = null;
                state.hasFetched = true;
                state.lastFetchedBusinessId = businessId;
                state.lastFetchedGroupId = groupId;

                const articlesList = action.payload.data || [];

                state.articles = articlesList.map(article => ({
                    ...article,
                    id: article._id,
                    // Map categoryIds array to a single category string for display compatibility
                    category: article.categoryIds && article.categoryIds.length > 0 ? article.categoryIds[0].name : '-',
                    // Ensure title is present (it is in the sample)
                    // Add isFeatured: true explicitly since these are featured articles
                    isFeatured: true
                }));
            })
            .addCase(fetchFeaturedArticles.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                // Keep stale data on background refresh failure to avoid empty flicker.
                // First-load failures will naturally stay as [].
            })
            .addCase(toggleArticleFeature.pending, (state, action) => {
                const { articleId, isFeatured } = action.meta.arg;
                if (!isFeatured) {
                    // Optimistic remove
                    state.articles = state.articles.filter(a => a.id !== articleId);
                }
            });
    }
});

export const { resetFeaturedArticles, clearFeaturedError } = featuredArticlesSlice.actions;

export default featuredArticlesSlice.reducer;
